import * as THREE from 'three';
import { planets } from './Planets';
import { getActiveCamera, getSpotlight } from './Initial3DSetup';
import { updateLobbyWalk } from './LobbyWalkControls';
import { facingQuaternion } from '../../Utils/labelFacing';
import { createCornerLogo, updateCornerLogo } from './CornerLogo';
import { createNavBar, updateNavBar } from './NavBar';

let renderer;
let scene;

/**
 * The coin and the row, in a scene of their own, drawn in a second pass over the world.
 *
 * Not parented to the camera, which is the obvious way to hold something in a corner and is not
 * enough here: a child of the camera is still depth-tested against the world, and the coin sits
 * four units out — close enough that walking into a lobby wall or into a ring of artwork puts
 * geometry in front of it and takes the mark off the frame. A scene of its own with the depth
 * buffer cleared between the two passes is what makes "in front of everything" true by
 * construction instead of true whenever the camera happens to be far from anything.
 */
let hudScene = null;
let cornerLogo = null;
let navBar = null;

/** Radians turned on its own axis each frame. The Sun barely turns at all. */
const SUN_SPIN = 0.0005;
const PLANET_SPIN = 0.005;


/**
 * @param {(planet: THREE.Object3D) => void} onSelect - where a category button's press goes.
 *   MainScene passes `switchPlanet`, which is the one function that knows both what a selection
 *   is and what is currently selected — so the difference between this control and a planet
 *   click lives there, beside the code that makes it, rather than here.
 * @param {() => boolean} isLive - whether the HUD is a control yet. MainScene passes `doorsOpen`:
 *   the coin is furniture while the lobby still has the pointer, and becomes the toggle for the
 *   row once the doors are open.
 * @param {() => void} onHome - what shutting the row means beyond shuttering it. MainScene passes
 *   `goBack`, its own Escape. Handed over for the same reason `isLive` is: what is open, and so
 *   what "back" is the next level of, is MainScene's to know.
 */
export function setAnimation(rend, scn, onSelect, isLive, onHome) {
    renderer = rend;
    scene = scn;
    hudScene = new THREE.Scene();

    // Built here rather than in the scene's own constructor: this is the call that hands over
    // the scene the coin is added to, and it is made once per page load.
    cornerLogo = createCornerLogo();
    hudScene.add(cornerLogo);

    // Handed the coin rather than the numbers behind it — the row lays itself out against where
    // the coin actually ended up, and a press on the coin is what opens it. `isLive` is
    // the same shape: MainScene owns the answer to whether the lobby is done with, and hands the
    // question over rather than the number behind it.
    navBar = createNavBar(cornerLogo, onSelect, isLive, onHome);
    hudScene.add(navBar);

    // The row's numbers, on a panel, in a development build and nowhere else.
    //
    // `process.env.NODE_ENV` is not read at run time: webpack's DefinePlugin replaces the expression
    // with the literal string while it is bundling, so a production build compiles this whole block
    // to `if ('production' === 'development')`, drops the branch with the rest of its dead code, and
    // never emits `DevGui.js` as a chunk. What that leaves behind is a page with no panel and no
    // panel in the bundle — which is the claim, and it is checked against the built output rather
    // than trusted: see the build note in this file's reader, not this comment.
    //
    // Imported here rather than at the top of the file because a static import would be followed by
    // the bundler whatever the branch said, which is exactly the thing being avoided.
    if (process.env.NODE_ENV === 'development') {
        import('./DevGui').then(module => module.attachNavGui());
    }
}

export function animate() {

    requestAnimationFrame(animate);
    
    if (planets.children.length > 0) {

        planets.children.forEach(planetGroup => {

            ////Planets Spin/////////
            const planet = planetGroup.children[0];
            if (!planet) return;

            const spin = planet.name === 'Sun_mesh' ? SUN_SPIN : PLANET_SPIN;

            planet.rotateY(spin); // spin on its axis

            // Anything hanging off the mesh turns with it, so a child that should turn
            // more slowly has to give part of that spin back. The factor is the share the
            // child takes for itself, on top of the whole of the parent's.
            planet.children.forEach(child => {
                if (child.userData.spinFactor) child.rotateY(spin * child.userData.spinFactor);
            });
        });
    }

    // Walking the lobby, if it is open and a key is down. It does its own nothing-check.
    updateLobbyWalk();

    // After the walk, not before: the walk writes the camera's orientation for this frame,
    // and the labels are turned to face wherever the camera has just been pointed.
    faceLabels();

    // Same reason, and it places itself from the camera rather than being parented to it — so
    // it too has to be told where the camera ended up, after the walk has moved it.
    updateCornerLogo(cornerLogo, getActiveCamera());

    // Strictly after the line above and not merely beside it: the row reads its plane, its corner
    // and its size off the coin, which that call has just written for this frame.
    updateNavBar(navBar, getActiveCamera());

    render();
};

const parentWorldQuaternion = new THREE.Quaternion();
const cameraWorldQuaternion = new THREE.Quaternion();
const labelFacing = new THREE.Quaternion();

/**
 * Each planet's label mesh, remembered instead of looked up again on every frame.
 *
 * `planetGroup.getObjectByName(name)` is a recursive walk of the whole subtree, and it was
 * being run for all nine planets sixty times a second to re-derive something that never
 * changes: Planets.js gives each planet exactly one label as it builds it (addPlanet adds
 * the mesh createText returned), and nothing afterwards removes, replaces or re-parents a
 * label — the only `scene.remove` anywhere in the 3D chain takes the lobby's gate. So the
 * reference is a constant, and the walk was pure waste.
 *
 * The lookup stays a `getObjectByName` rather than becoming a scan of `children`: that the
 * label is a direct child of the planet group today is Planets.js's business and not this
 * file's, and looking it up by name keeps working if it stops being one.
 *
 * Rebuilt rather than merely cached, because the scene is built asynchronously — the planets
 * only appear once loadCelestialBodies's awaits have resolved, which is why `animate()`
 * guards on `planets.children.length` in the first place. The cache is refilled whenever the
 * planet count changes, and again whenever it holds fewer labels than there are planets:
 * `userData.labelName` is set by AddImages' setPlanet as each planet is built, not by the
 * planet's own constructor, so a planet glimpsed before its label exists is looked at again
 * next frame instead of being remembered as label-less forever. That second condition costs
 * nothing once the scene is whole, because the two counts then agree and the walk stops; a
 * planet that genuinely never received a label would refill every frame, which is only what
 * this file did before.
 */
let cachedLabels = null;
let cachedLabelPlanetCount = -1;

function getPlanetLabels() {
    const planetCount = planets.children.length;

    if (cachedLabels !== null &&
        cachedLabelPlanetCount === planetCount &&
        cachedLabels.length === planetCount) {
        return cachedLabels;
    }

    const labels = [];

    planets.children.forEach(planetGroup => {
        const label = planetGroup.getObjectByName(planetGroup.userData.labelName);

        // The old per-frame `if (!label) return;` is this line, moved to build time: a
        // planet with no label is left out here rather than cached as an empty slot, so
        // nothing downstream has to test for one.
        if (label) labels.push(label);
    });

    cachedLabels = labels;
    cachedLabelPlanetCount = planetCount;

    return labels;
}

/**
 * Turns every label so its letters face the camera, once per frame.
 *
 * The letters are flat, so a label is only readable while its face is turned back towards
 * the camera — and that is true whatever is selected and whatever is moving, so this runs
 * unconditionally. It used to be a yaw about the label's own Y, turned by an angle worked
 * out from the label's *local* position rather than from where it stands in the world, and
 * it was skipped whenever a planet was in focus. Between them those two faults left the
 * letters on every planet but the sun sitting at the wrong angle, and put them edge on
 * whenever anything was selected.
 *
 * World quaternions on both sides, because the label's own is relative to a parent that sits
 * out in the system at its own angle (see facingQuaternion).
 *
 * Which label belongs to which planet is not re-derived here at all — see getPlanetLabels
 * for why that reference is a constant and how the cache that holds it is kept honest. What
 * remains per frame is only the turning, which genuinely does change every frame.
 */
function faceLabels() {
    if (planets.children.length === 0) return;

    const camera = getActiveCamera();
    camera.getWorldQuaternion(cameraWorldQuaternion);

    const labels = getPlanetLabels();

    // Indexed, not for..of and not forEach: both of those hand back a fresh iterator or
    // closure per call, and this runs on every frame.
    for (let i = 0; i < labels.length; i++) {
        const label = labels[i];

        // The parent quaternion is read per label and not hoisted out of the loop. It is
        // tempting — the parent is the planet group in every case here — but each label's
        // own quaternion is relative to the object it is actually parented to, and those
        // sit round the sun each at its own angle, so one read would not serve the others.
        label.parent.getWorldQuaternion(parentWorldQuaternion);

        label.quaternion.copy(facingQuaternion(parentWorldQuaternion, cameraWorldQuaternion, labelFacing));
    }
}

/**
 * The world, then the HUD over it, in two passes with the depth buffer cleared between them.
 *
 * Both depth clears are wanted, and the first one is not decoration.
 *
 * `renderer.autoClear` is false by the time the animation starts (MainScene turns it off), and
 * the scene's background is a cube map rather than a colour — read off three 0.175's
 * `WebGLBackground`: `forceClear` is set for `isColor` and nothing else, and the clear itself
 * needs `autoClear || forceClear`. So the branch is never taken, and the only thing the
 * background does each frame is repaint the colour buffer: its box is `depthTest: false,
 * depthWrite: false`. That leaves the depth buffer to accumulate — the depth test is LESS, so
 * every pixel keeps the nearest value any frame has ever written there, and the world erodes as
 * the camera flies. Clearing once a frame is what a renderer with `autoClear` left on would have
 * been doing anyway, and the second pass then needs its own clear after it, or the coin and the
 * row would be the sticky depth that the world is measured against on the next frame.
 *
 * The second pass is a second traversal of a scene holding ten objects, which is the whole cost
 * of the arrangement; it is not the world being walked twice.
 */
export function render() {
    const camera = getActiveCamera();

    renderer.clearDepth();
    renderer.render(scene, camera);

    renderer.clearDepth();
    renderer.render(hudScene, camera);
};

export function updateSpotlightTarget(target) {
    const spot = getSpotlight();

    spot.intensity = target.userData.spotlightIntensity;

    spot.target = target;
}
